import {
  createDateFormatter,
  dateFromDisplayParts,
  displayDate,
  type TimezoneMode,
} from "../lib/dateTime";
import { useDisplayTimezone } from "../lib/DateDisplayContext";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { CalendarDays, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./Button";
import { Field } from "./Field";
import { Input } from "./FormControls";
import { IconButton } from "./IconButton";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { cn } from "../lib/cn";
import { formatCompactDateRange, isFullDayDateRange } from "../lib/dateRange";

export interface DateRangeValue {
  start: string;
  end: string;
}
export interface DateRangePickerProps {
  value: DateRangeValue;
  onChange: (value: DateRangeValue) => void;
  label?: string;
  className?: string;
}
const DAY_MS = 86400000;
const dateKey = (date: Date) => date.toISOString().slice(0, 10);
const parseDay = (day: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return new Date(NaN);
  const date = new Date(`${day}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && dateKey(date) === day ? date : new Date(NaN);
};
const timePattern = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;
function formatInput(value: string, kind: "date" | "time") {
  const digits = value.replace(/\D/g, "").slice(0, kind === "date" ? 8 : 6);
  const first = kind === "date" ? 4 : 2;
  const separator = kind === "date" ? "-" : ":";
  return (
    digits.slice(0, first) +
    (digits.length > first ? separator + digits.slice(first, first + 2) : "") +
    (digits.length > first + 2 ? separator + digits.slice(first + 2) : "")
  );
}
const shiftDay = (day: string, amount: number) => {
  const time = parseDay(day).getTime();
  return Number.isFinite(time) ? dateKey(new Date(time + amount * DAY_MS)) : day;
};
const monthOf = (day: string) => {
  const date = parseDay(day);
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
};
function draftOf(value: DateRangeValue, timezone: TimezoneMode) {
  const start = displayDate(new Date(value.start), timezone);
  const end = displayDate(new Date(value.end), timezone);
  const fullDays = isFullDayDateRange(start, end);
  const nextMidnight = fullDays && end.getUTCHours() === 0;
  return {
    start: dateKey(start),
    end: dateKey(nextMidnight ? new Date(end.getTime() - 1) : end),
    startTime: start.toISOString().slice(11, 19),
    endTime: fullDays ? "23:59:59" : end.toISOString().slice(11, 19),
  };
}

type RangeDraft = ReturnType<typeof draftOf>;

function RangeCalendar({
  start,
  end,
  onSelect,
}: {
  start: string;
  end: string;
  onSelect: (day: string) => void;
}) {
  const initialDay = Number.isFinite(parseDay(start).getTime()) ? start : dateKey(new Date());
  const [month, setMonth] = useState(() => monthOf(initialDay));
  const [focusedDay, setFocusedDay] = useState(initialDay);
  const calendarRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!Number.isFinite(parseDay(start).getTime())) return;
    setMonth((current) => {
      const afterPair = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 2, 1));
      const selected = parseDay(start);
      return selected >= current && selected < afterPair ? current : monthOf(start);
    });
    setFocusedDay(start);
  }, [start]);
  const months = [month, new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 1))];
  function moveMonth(amount: number) {
    const next = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + amount, 1));
    setMonth(next);
    setFocusedDay(dateKey(next));
  }
  function moveFocus(event: KeyboardEvent<HTMLButtonElement>, day: string) {
    const offset = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }[event.key];
    if (offset === undefined) return;
    event.preventDefault();
    const next = shiftDay(day, offset);
    setFocusedDay(next);
    const date = parseDay(next);
    const afterPair = new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 2, 1));
    if (date < month || date >= afterPair) setMonth(monthOf(next));
    requestAnimationFrame(() =>
      calendarRef.current?.querySelector<HTMLButtonElement>(`[data-date="${next}"]`)?.focus(),
    );
  }
  return (
    <div ref={calendarRef} className="grid grid-cols-1 gap-4 min-[560px]:grid-cols-2">
      {months.map((visibleMonth, monthIndex) => {
        const monthLabel = createDateFormatter("utc", {
          month: "short",
          year: "numeric",
          timeZone: "UTC",
        }).format(visibleMonth);
        const firstCell = new Date(
          visibleMonth.getTime() - ((visibleMonth.getUTCDay() + 6) % 7) * DAY_MS,
        );
        return (
          <div key={visibleMonth.toISOString()} role="group" aria-label={monthLabel}>
            <div className="mb-2 grid h-10 grid-cols-[2.5rem_1fr_2.5rem] items-center gap-2">
              {monthIndex === 0 ? (
                <IconButton label="Previous month" onClick={() => moveMonth(-1)}>
                  <ChevronLeft className="size-4" aria-hidden />
                </IconButton>
              ) : (
                <span />
              )}
              <span aria-live="polite" className="text-center text-sm font-medium text-primary">
                {monthLabel}
              </span>
              {monthIndex === 1 ? (
                <IconButton label="Next month" onClick={() => moveMonth(1)}>
                  <ChevronRight className="size-4" aria-hidden />
                </IconButton>
              ) : (
                <span />
              )}
            </div>
            <div className="grid grid-cols-7 gap-0.5" aria-hidden>
              {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
                <span key={day} className="py-2 text-center text-xs text-muted">
                  {day}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: 42 }, (_, index) => {
                const date = new Date(firstCell.getTime() + index * DAY_MS);
                const day = dateKey(date);
                if (date.getUTCMonth() !== visibleMonth.getUTCMonth())
                  return (
                    <span
                      key={day}
                      aria-hidden
                      className="flex h-10 items-center justify-center text-sm text-muted"
                    >
                      {date.getUTCDate()}
                    </span>
                  );
                const edge = day === start || day === end;
                const selected = day >= start && day <= end;
                return (
                  <button
                    key={day}
                    type="button"
                    data-date={day}
                    aria-label={createDateFormatter("utc", {
                      dateStyle: "full",
                      timeZone: "UTC",
                    }).format(date)}
                    aria-pressed={selected}
                    tabIndex={day === focusedDay ? 0 : -1}
                    onFocus={() => setFocusedDay(day)}
                    onKeyDown={(event) => moveFocus(event, day)}
                    onClick={() => {
                      setFocusedDay(day);
                      onSelect(day);
                    }}
                    className={cn(
                      "h-10 min-w-0 rounded-control text-sm tabular-nums focus-visible:outline-2 focus-visible:outline-focus",
                      edge
                        ? "bg-brand-navy text-white dark:bg-brand-cyan dark:text-brand-navy"
                        : selected
                          ? "bg-brand-cyan/15 text-primary"
                          : "text-primary hover:bg-surface-elevated",
                    )}
                  >
                    {date.getUTCDate()}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RangeFields({
  draft,
  onChange,
}: {
  draft: RangeDraft;
  onChange: (value: RangeDraft) => void;
}) {
  return (
    <div className="mt-3 grid grid-cols-1 gap-3 border-t border-border pt-3 min-[560px]:grid-cols-2">
      {(["start", "end"] as const).map((boundary) => (
        <Field key={boundary} label={boundary === "start" ? "Start date" : "End date"}>
          <div className="grid grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-2">
            <Input
              type="text"
              inputMode="numeric"
              placeholder="YYYY-MM-DD"
              pattern="\d{4}-\d{2}-\d{2}"
              maxLength={10}
              aria-label={boundary === "start" ? "Start date" : "End date"}
              aria-invalid={!Number.isFinite(parseDay(draft[boundary]).getTime())}
              value={draft[boundary]}
              onChange={(event) =>
                onChange({ ...draft, [boundary]: formatInput(event.target.value, "date") })
              }
            />
            <Input
              type="text"
              inputMode="numeric"
              placeholder="HH:MM:SS"
              pattern="([01]\d|2[0-3]):[0-5]\d:[0-5]\d"
              maxLength={8}
              aria-label={boundary === "start" ? "Start time" : "End time"}
              aria-invalid={!timePattern.test(draft[`${boundary}Time`])}
              value={draft[`${boundary}Time`]}
              onChange={(event) =>
                onChange({ ...draft, [`${boundary}Time`]: formatInput(event.target.value, "time") })
              }
              onBlur={() => {
                const time = draft[`${boundary}Time`];
                if (/^([01]\d|2[0-3]):[0-5]\d$/.test(time))
                  onChange({ ...draft, [`${boundary}Time`]: `${time}:00` });
              }}
            />
          </div>
        </Field>
      ))}
    </div>
  );
}

export function DateRangePicker({
  value,
  onChange,
  label = "Date range",
  className,
}: DateRangePickerProps) {
  const timezone = useDisplayTimezone();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => draftOf(value, timezone));
  const [pickingEnd, setPickingEnd] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const previousTimezone = useRef(timezone);
  useEffect(() => {
    if (previousTimezone.current !== timezone) {
      previousTimezone.current = timezone;
      setDraft(draftOf(value, timezone));
    }
  }, [timezone, value]);
  const start = dateFromDisplayParts(draft.start, draft.startTime, timezone, value.start);
  const end =
    draft.startTime === "00:00:00" && draft.endTime === "23:59:59"
      ? dateFromDisplayParts(shiftDay(draft.end, 1), "00:00:00", timezone, value.end)
      : dateFromDisplayParts(draft.end, draft.endTime, timezone, value.end);
  const inputsValid =
    Number.isFinite(parseDay(draft.start).getTime()) &&
    Number.isFinite(parseDay(draft.end).getTime()) &&
    timePattern.test(draft.startTime) &&
    timePattern.test(draft.endTime);
  const valid = Number.isFinite(start.getTime()) && Number.isFinite(end.getTime()) && end > start;
  function changeOpen(next: boolean) {
    if (next) {
      setDraft(draftOf(value, timezone));
      setPickingEnd(false);
    }
    setOpen(next);
  }
  function chooseDay(day: string) {
    setDraft((current) =>
      pickingEnd
        ? {
            ...current,
            start: day < current.start ? day : current.start,
            end: day < current.start ? current.start : day,
          }
        : { ...current, start: day, end: day },
    );
    setPickingEnd(!pickingEnd);
  }
  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className={cn("w-full justify-between gap-2", className)}
          aria-label={label}
        >
          <CalendarDays className="size-4 shrink-0 text-muted" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-left">
            {formatCompactDateRange(new Date(value.start), new Date(value.end), timezone)}
          </span>
          <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        aria-label={label}
        collisionPadding={8}
        className="max-h-[calc(100dvh-2rem)] w-[min(42rem,calc(100vw-1rem))] overflow-y-auto p-3"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          requestAnimationFrame(() =>
            contentRef.current
              ?.querySelector<HTMLButtonElement>("[data-date][tabindex='0']")
              ?.focus(),
          );
        }}
      >
        <RangeCalendar start={draft.start} end={draft.end} onSelect={chooseDay} />
        <RangeFields
          draft={draft}
          onChange={(next) => {
            setDraft(next);
            setPickingEnd(false);
          }}
        />
        {!valid ? (
          <p role="alert" className="mt-3 text-sm text-status-warn-text">
            {inputsValid
              ? "End must be later than start."
              : "Enter valid dates (YYYY-MM-DD) and times (HH:MM:SS)."}
          </p>
        ) : null}
        <div className="mt-3 flex justify-end gap-2 border-t border-border pt-3">
          <Button size="sm" variant="ghost" onClick={() => changeOpen(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!valid}
            onClick={() => {
              onChange({ start: start.toISOString(), end: end.toISOString() });
              changeOpen(false);
            }}
          >
            Apply
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
